/**
 * Блок «Поля клиента» в карточке клиента (UC-1 пп. 4–5) — только факты.
 *
 * Сводка видна ДО входа в кабинет клиента: иначе, чтобы узнать «есть ли у него
 * вообще поля», приходится заходить к каждому по очереди.
 *
 * Кнопок здесь больше нет. Три действия («Завести хозяйство» · «Добавить поля»
 * · «Показать поля на карте») живут одним набором в шапке карточки — ровно так
 * же, как в строке списка (шаг 4 флоу). Второй набор тех же кнопок ниже по
 * странице означал бы, что человек ищет, какой из них «правильный».
 */
import { MapPin } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { lastSceneLabel } from '@/lib/consultant/format';
import type { ClientFieldsSummary } from '@/lib/consultant/types';

export interface ClientFieldsCardProps {
  clientName: string;
  summary: ClientFieldsSummary | null;
  loading?: boolean;
}

export function ClientFieldsCard({ clientName, summary, loading }: ClientFieldsCardProps) {
  if (loading) {
    return (
      <Card className="space-y-2 p-4" data-testid="client-fields-loading">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-12 w-full" />
      </Card>
    );
  }

  const count = summary?.fields_count ?? 0;

  return (
    <Card className="space-y-3 p-4" data-testid="client-fields-card">
      <p className="text-sm font-semibold">Поля клиента</p>

      {count === 0 ? (
        <p className="text-sm text-muted-foreground">
          У клиента {clientName} пока нет полей. Обведите поля на карте или
          загрузите файл с готовыми границами — снимки со спутника подтянутся
          сами. Рабочий кабинет клиента создастся с первым полем.
        </p>
      ) : (
        <p className="text-sm">
          <b>
            {count} полей · {Math.round(summary?.area_ha ?? 0)} га
          </b>
          <br />
          <span className="text-muted-foreground">
            {lastSceneLabel(summary?.last_scene_date ?? null)}
          </span>
        </p>
      )}

      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <MapPin className="h-3 w-3" />
        Поля клиента лежат в его кабинете — открываются кнопкой «Показать на
        карте» вверху.
      </p>
    </Card>
  );
}
